import type { NextPage } from "next";
import React, { useState, useEffect } from "react";
import { Formik, Form, FormikErrors } from "formik";
import { useRouter } from "next/router";
import validator from "validator";
import type { loginInput } from "../types/login";
import {
	useLoginMutation,
	useLoginSubSubscription,
} from "../lib/graphql/login.graphql";
import { LoadingCenter } from "../components/loading";
import { GetAuth, SetAuth } from "../lib/auth";
import toast from "react-hot-toast";
import { generate as randomstring } from "randomstring";
import QRCode from "react-qr-code";
import {
	Center,
	ContainerWhite,
	WhiteBox,
	FlexCenter,
	CenterWithLogo,
	QrPadding,
	WhiteLink,
	SplitLine,
} from "../components/basic";
import { Field, Submit } from "../components/form";
import { Pages } from "../lib/pages";

const Login: NextPage = () => {
	const router = useRouter();
	const [loginMutation] = useLoginMutation();
	const [qrLogin] = useState(randomstring(25));
	const [initialValues, setInitialValues] = useState<loginInput>({
		email: "",
		password: "",
	});

	const { data, error } = useLoginSubSubscription({
		variables: {
			listen: qrLogin,
		},
	});
	const [loaded, setLoaded] = useState(false);

	const validate = (values: loginInput) => {
		const errors: FormikErrors<loginInput> = {};
		if (!validator.isEmail(values.email)) errors.email = "Špatný formát emailu";
		if (!values.password) errors.password = "Heslo je povinné";
		return errors;
	};

	const submit = async (values: loginInput) => {
		setInitialValues(values);
		try {
			setLoaded(false);
			const { data } = await loginMutation({
				variables: {
					email: values.email,
					password: values.password,
				},
			});
			SetAuth("Bearer " + data.login);
			router.reload();
		} catch (error) {
			setLoaded(true);
			toast.error(error.message);
		}
	};

	useEffect(() => {
		if (GetAuth()) router.push(Pages.Index);
		else setLoaded(true);
	}, [router]);

	useEffect(() => {
		if (data) {
			SetAuth("Bearer " + data.qrLogin);
			router.reload();
		}
	}, [data, router]);

	if (error)
		return (
			<ContainerWhite>
				<Center>
					<p>{error.message}</p>
				</Center>
			</ContainerWhite>
		);
	if (!loaded)
		return (
			<ContainerWhite>
				<LoadingCenter />
			</ContainerWhite>
		);

	return (
		<ContainerWhite>
			<CenterWithLogo>
				<WhiteBox>
					<Formik
						initialValues={initialValues}
						onSubmit={submit}
						validate={validate}>
						{({ errors, touched, isValid }) => (
							<Form>
								<FlexCenter>
									<Field
										name="email"
										placeholder="Email"
										type="email"
										errors={errors.email}
										touched={touched.email}
									/>
									<Field
										name="password"
										placeholder="Heslo"
										type="password"
										errors={errors.password}
										touched={touched.password}
									/>
									<Submit isValid={isValid}>Přihlásit se</Submit>
								</FlexCenter>
							</Form>
						)}
					</Formik>
					<SplitLine />
					<QrPadding>
						<QRCode value={qrLogin} size={200} />
					</QrPadding>
					<WhiteLink href={Pages.Register}>Zaregistrovat se</WhiteLink>
					<WhiteLink href={Pages.SendEmail}>Zapomenuté heslo</WhiteLink>
				</WhiteBox>
			</CenterWithLogo>
		</ContainerWhite>
	);
};

export default Login;
